import React from 'react';
import PropTypes from 'prop-types';
import ClassNames from 'classnames';
import { FilePickerState } from './FilePickerState';
import styles from './styles.scss';

class FilePicker extends React.Component {
    static preventDefaults(e) {
        e.preventDefault();
        e.stopPropagation();
    }

    constructor(props) {
        super(props);
        this.state = {
            hover: false,
        };

        // This binding is necessary to make 'this' work in the callback
        this.handleDragEnter = this.handleDragEnter.bind(this);
        this.handleDragLeave = this.handleDragLeave.bind(this);
        this.handleDrop = this.handleDrop.bind(this);
        this.handleMouseEnter = this.handleMouseEnter.bind(this);
        this.handleMouseLeave = this.handleMouseLeave.bind(this);
        this.getRectangularFilePicker = this.getRectangularFilePicker.bind(this);
        this.getRoundFilePicker = this.getRoundFilePicker.bind(this);
    }

    componentDidMount() {
        const { preview, passerror, editfile } = this.props;

        // We need to add the event handlers to child elements of the dropArea
        // then filter out when we should be firing the event handlers through event.target
        // and event.fromElement in the dragleave eventListener...

        // This is because when we drag over a child element -
        // it fires the onDragLeave event on the parent element

        // Prevent default drag behaviors
        ['dragenter', 'dragover', 'dragleave', 'drop', 'mouseenter', 'mouseleave'].forEach((eventName) => {
            document.body.addEventListener(eventName, FilePicker.preventDefaults, false);
            this.dropArea.addEventListener(eventName, FilePicker.preventDefaults, false);
        });

        // Highlight drop area when item is dragged over it
        this.dropArea.addEventListener('dragenter', this.handleDragEnter, false);
        this.dropArea.addEventListener('dragleave', this.handleDragLeave, false);
        this.dropArea.addEventListener('drop', this.handleDragLeave, false);
        this.dropArea.addEventListener('drop', this.handleDrop, false);
        if (preview) {
            this.dropArea.addEventListener('mouseenter', this.handleMouseEnter, false);
            this.dropArea.addEventListener('mouseleave', this.handleMouseLeave, false);
        }

        if (this.browse) {
            ['dragenter', 'dragover', 'drop', 'mouseenter'].forEach((eventName) => {
                this.browse.addEventListener(eventName, FilePicker.preventDefaults, false);
            });
            this.browse.addEventListener('dragenter', this.handleDragEnter, false);
            this.browse.addEventListener('drop', this.handleDragLeave, false);
            this.browse.addEventListener('drop', this.handleDrop, false);
            if (preview) {
                this.browse.addEventListener('mouseenter', this.handleMouseEnter, false);
            }
        }

        // if is editfile, validate the file upload in the parent form.
        if (editfile) {
            passerror('');
        }
    }

    handleDragEnter() {
        this.setState(() => ({
            eventState: FilePickerState.drag,
        }));
    }

    handleDragLeave(e) {
        const { eventState, hasFile, error } = this.state;

        let nextState = FilePickerState.uploaded;
        if (eventState === FilePickerState.drag) {
            if ((e.target !== this.dropArea || e.fromElement !== this.browse)
                && (e.target !== this.browse || e.fromElement !== this.dropArea)) {
                // Reset the state to the state it was before onDragEnter
                if (!hasFile) {
                    nextState = FilePickerState.start;
                }

                if (error) {
                    nextState = FilePickerState.failed;
                }

                this.setState(() => ({
                    eventState: nextState,
                }));
            }
        }
    }

    /// Event from the hidden file <input>
    handleBrowse(e) {
        const { handleFileChanged } = this.props;

        FilePicker.preventDefaults(e);
        const file = e.target.files[0];

        handleFileChanged(file);
    }

    /// Event from the drop area <div>
    handleDrop(e) {
        const { eventState, handleFileChanged } = this.props;

        if (eventState !== FilePickerState.uploading) {
            const dt = e.dataTransfer;
            const file = dt.files[0];

            handleFileChanged(file);
        }
    }

    handleMouseEnter() {
        const { preview, hasFile } = this.props;
        if (hasFile && preview) {
            this.setState(() => ({
                hover: true,
            }));
        }
    }

    handleMouseLeave() {
        this.setState(() => ({
            hover: false,
        }));
    }

    // get round logo or square logo
    getRoundFilePicker(filePickerProps) {
        const { hover } = this.state;
        const {
            name, optional, square,
            hasFile, logoUrlUnchanged, eventState, handleRemoveFile,
        } = this.props;
        const {
            dragClass,
            icon,
            dropText,
            browseInput,
            inputValue,
            subtitle,
            previewAttr,
        } = filePickerProps;

        const background = hover ? 'linear-gradient(0deg, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5))' : '';

        const shapeStyle = square ? styles.square : styles.round;

        let customInput = null;

        // for edit logoUrl, if logoUrlUnchanged is true which means user didn't change logo,
        // set name as 'logoUnchanged' so input value won't be used
        customInput = (
            <input type="hidden" name={logoUrlUnchanged ? 'logoUnchanged' : name} id={name} value={inputValue} />
        );

        // when subtitle is null, subtitleView won't take up place
        let subtitleView = null;
        if (subtitle) {
            subtitleView = (
                <span className={ClassNames(styles.subtitle, { [`${styles.primaryText}`]: eventState !== 'start' })}>
                    {subtitle}
                </span>
            );
        }

        // only show Remove button when hasFile is true
        let removeFile = null;
        if (hasFile && eventState !== 'start' && optional) {
            removeFile = (
                <span
                    role="button"
                    tabIndex={0}
                    className={`${styles.subtitle} ${styles.removeText}`}
                    onClick={() => { handleRemoveFile(); }}
                    data-testid="remove-file"
                >
                    Remove
                </span>
            );
        }

        return (
            <div>
                <div className={styles.filepicker}>
                    <div
                        ref={(ref) => { this.dropArea = ref; }}
                        className={`${dragClass} ${shapeStyle}`}
                        data-testid={`${name}-filepicker`}
                        style={previewAttr}
                    >
                        <div className={`${shapeStyle} ${styles['image-overlay']}`} style={{ background }}>
                            {icon}
                            {dropText}
                        </div>
                        {browseInput}
                        {customInput}
                    </div>
                    {subtitleView}
                    {removeFile}
                </div>
            </div>
        );
    }

    getRectangularFilePicker(filePickerProps) {
        const { name } = this.props;
        const {
            dragClass,
            icon,
            dropText,
            browseInput,
            inputValue,
            subtitle,
            previewAttr,
        } = filePickerProps;

        return (
            <div className={styles.filepicker}>
                <div
                    ref={(ref) => { this.dropArea = ref; }}
                    className={dragClass}
                    data-testid={`${name}-filepicker`}
                    style={previewAttr}
                >
                    <div>
                        {icon}
                        {dropText}
                        <p>{subtitle}</p>
                        {browseInput}
                    </div>
                    <input type="hidden" name={name} id={name} value={inputValue} />
                </div>
            </div>
        );
    }

    render() {
        const {
            hover,
        } = this.state;
        const {
            round, preview, eventState, hasFile, fileName, backgroundimage,
            filePickerInputValue, filePickerDefaultSubtitle,
        } = this.props;

        // inputValue cannot be undefined as it'll change the input to be uncontrolled.
        // let's set it here to be sure that never happens.
        const filePickerProps = {
            subtitle: filePickerDefaultSubtitle,

            // set the highlight class when drag is fired
            dragClass: eventState === 'drag' ? styles.drag : '',
        };

        if (hasFile) {
            // hasFile and eventState is start means just removed file
            // subtitle should be initial value aboved
            if (eventState !== 'start') {
                filePickerProps.subtitle = fileName;
            }

            // set preview image if we have specified to have it
            if (preview) {
                filePickerProps.previewAttr = {
                    backgroundImage: backgroundimage,
                    backgroundSize: 'cover',
                    backgroundPosition: 'top center',
                };
            }
        }
        filePickerProps.inputValue = filePickerInputValue;

        if (eventState === 'uploading') {
            filePickerProps.subtitle = 'uploading...';
        }

        // the only time we want to NOT show the icon and the texts is when the filepicker
        // has a preview image and is not being hovered over.

        // hover should only be set while hasFile is true
        // or after removed file when hasFile is true and eventState is 'start'
        if ((!(preview && hasFile && !hover)) || (hasFile && eventState === 'start')) {
            const hoverClass = hover ? styles['hover-text'] : '';

            filePickerProps.icon = (
                <svg width="22" height="18" viewBox="0 0 22 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11 13L15 9H12V0H10V9H7L11 13ZM20 0H14V1.99H20V16.02H2V1.99H8V0H2C0.9 0 0 0.9 0 2V16C0 17.1 0.9 18 2 18H20C21.1 18 22 17.1 22 16V2C22 0.9 21.1 0 20 0ZM11 13L15 9H12V0H10V9H7L11 13ZM20 0H14V1.99H20V16.02H2V1.99H8V0H2C0.9 0 0 0.9 0 2V16C0 17.1 0.9 18 2 18H20C21.1 18 22 17.1 22 16V2C22 0.9 21.1 0 20 0Z" fill={hover ? '#f2f2f2' : '#323232'} />
                </svg>
            );

            filePickerProps.dropText = (
                <h6 className={hoverClass} data-testid="drop-text">
                    {'Drag and drop or  '}
                    <span
                        role="button"
                        tabIndex={0}
                        ref={(ref) => { this.browse = ref; }}
                        onClick={() => { this.fileinput.click(); }}
                    >
                        Browse
                    </span>
                    {round ? ' file' : ' for a file'}
                </h6>
            );
        }

        filePickerProps.browseInput = (
            <input
                type="file"
                id="file"
                ref={(ref) => { this.fileinput = ref; }}
                onChange={this.handleBrowse.bind(this)}
                style={{ display: 'none' }}
                data-testid="hidden-file-input"
            />
        );

        return round
            ? this.getRoundFilePicker(filePickerProps)
            : this.getRectangularFilePicker(filePickerProps);
    }
}

FilePicker.propTypes = {
    handleFileChanged: PropTypes.func.isRequired,
    name: PropTypes.string.isRequired,
    round: PropTypes.bool,
    square: PropTypes.bool,
    preview: PropTypes.bool,
    editfile: PropTypes.bool,
    fileName: PropTypes.string,
    optional: PropTypes.bool,
    hasFile: PropTypes.bool,
    logoUrlUnchanged: PropTypes.bool,
    eventState: PropTypes.string.isRequired,
    filePickerInputValue: PropTypes.string,
    backgroundimage: PropTypes.string,
    passerror: PropTypes.func,
    handleRemoveFile: PropTypes.func.isRequired,
    filePickerDefaultSubtitle: PropTypes.string.isRequired,
};

FilePicker.defaultProps = {
    passerror: () => { },
    round: false,
    square: false,
    preview: false,
    editfile: false,
    optional: false,
    logoUrlUnchanged: true,
    hasFile: false,
    fileName: '',
    filePickerInputValue: '',
    backgroundimage: '',
};

export default FilePicker;
